import { useMemo, useCallback } from 'react';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import {
  demoOrganizations,
  demoUserProfile,
  getDemoOrganizationById as rawGetDemoOrganizationById,
  getDemoMembersByOrgId as rawGetDemoMembersByOrgId,
  getDemoCustomRolesByOrgId as rawGetDemoCustomRolesByOrgId,
  getCurrentUserRoleInOrg as rawGetCurrentUserRoleInOrg,
  getDemoInvitationsByOrgId as rawGetDemoInvitationsByOrgId,
} from '@/mockData/demoOrganizationData';
import type {
  DemoOrganization,
  DemoOrganizationMember,
  DemoCustomRole,
} from '@/mockData/demoOrganizationData';

export function useDemoOrgData() {
  const isDemo = useIsDemo();

  const organizations = useMemo(
    () => (isDemo ? demoOrganizations : []),
    [isDemo],
  );

  const userProfile = useMemo(
    () => (isDemo ? demoUserProfile : ({} as typeof demoUserProfile)),
    [isDemo],
  );

  const getOrganizationById = useCallback(
    (orgId: string): DemoOrganization | undefined =>
      isDemo ? rawGetDemoOrganizationById(orgId) : undefined,
    [isDemo],
  );

  const getMembersByOrgId = useCallback(
    (orgId: string): DemoOrganizationMember[] =>
      isDemo ? rawGetDemoMembersByOrgId(orgId) : [],
    [isDemo],
  );

  const getCustomRolesByOrgId = useCallback(
    (orgId: string): DemoCustomRole[] =>
      isDemo ? rawGetDemoCustomRolesByOrgId(orgId) : [],
    [isDemo],
  );

  const getCurrentUserRoleInOrg = useCallback(
    (orgId: string, currentUserId?: string): { role: string; customRoleName?: string } | null =>
      isDemo ? rawGetCurrentUserRoleInOrg(orgId, currentUserId) : null,
    [isDemo],
  );

  const getInvitationsByOrgId = useCallback(
    (orgId: string) =>
      isDemo ? rawGetDemoInvitationsByOrgId(orgId) : [],
    [isDemo],
  );

  return {
    organizations,
    userProfile,
    getOrganizationById,
    getMembersByOrgId,
    getCustomRolesByOrgId,
    getCurrentUserRoleInOrg,
    getInvitationsByOrgId,
  };
}
